import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { format } from "date-fns";
import { formatToTitleCase } from "@/utils/formatters/stringFormatters";
import { hasStraightLaser, type LaseredClaimantListItem } from "@/types";
import { laserKindLabel } from "@/utils/helpers/laserKind";
import { conditionDisplayState, conditionStateCountsText } from "@/utils/helpers/expectedTrigger";

// Styling mirrors src/resources/clients/components/aggregate-deductible/export/exportAggregateData.ts
// so lasered-claimant exports look consistent with the breach report.

const currencyFormat = '_("$"* #,##0.00_);_("$"* \\(#,##0.00\\);_("$"* "-"??_);_(@_)';

const COLORS = {
  primary: "FF2A7D99",
  primaryDark: "FF1E5C73",
  white: "FFFFFFFF",
  black: "FF1A1A1A",
  rowFillOdd: "FFF5F7FA",
  rowFillEven: "FFFFFFFF",
  borderLight: "FFD5DAE0",
} as const;

const SHEET_OPTIONS: Partial<ExcelJS.AddWorksheetOptions> = {
  views: [{ showGridLines: false }],
};

const thinBorder = (color: string = COLORS.borderLight): Partial<ExcelJS.Borders> => ({
  top: { style: "thin", color: { argb: color } },
  left: { style: "thin", color: { argb: color } },
  bottom: { style: "thin", color: { argb: color } },
  right: { style: "thin", color: { argb: color } },
});

const applyHeaderRow = (row: ExcelJS.Row, headers: string[]) => {
  row.height = 30;
  headers.forEach((header, idx) => {
    const cell = row.getCell(idx + 1);
    cell.value = header;
    cell.font = { bold: true, size: 10, color: { argb: COLORS.white } };
    cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: COLORS.primary } };
    cell.border = thinBorder(COLORS.primaryDark);
  });
};

const applyDataCellStyle = (cell: ExcelJS.Cell, fillColor: string, isCurrency = false) => {
  cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: fillColor } };
  cell.border = thinBorder();
  cell.font = { size: 10, color: { argb: COLORS.black } };
  if (isCurrency) cell.numFmt = currencyFormat;
};

const rowFill = (index: number) => (index % 2 === 0 ? COLORS.rowFillOdd : COLORS.rowFillEven);

const today = () => format(new Date(), "yyyyMMdd");

const claimantName = (r: LaseredClaimantListItem): string =>
  [r.claimant_first_name, r.claimant_last_name].filter(Boolean).join(" ") || r.claimant_name || "Unknown";

const COLUMN_WIDTHS = [30, 18, 16, 18, 18, 24, 24, 30, 18, 18, 20, 16, 14, 26];

const HEADERS = [
  "Name",
  "Member ID",
  "Laser Type",
  "Lasered Deductible",
  "Contract Deductible",
  "Contract Incurred Window",
  "Contract Paid Window",
  "Excluded Procedures (CPT)",
  "Excluded",
  "Effective Deductible",
  "Deductible Source",
  "Effective From",
  "Needs Review",
  "Conditional Lasers",
];

/** Server `effective_deductible_source` → export label (Feature #3502). */
const SOURCE_LABEL: Record<string, string> = {
  specific: "Specific",
  lasered: "Straight laser",
  conditional_laser: "Conditional laser",
  modification: "Modification",
  contract_laser: "Contract laser",
  contract_excluded: "Excluded",
  review_decision: "Review decision",
};

/** Same counts as the tab's Conditional Lasers column, expected triggers included (#7361). */
const conditionCounts = (r: LaseredClaimantListItem): string => conditionStateCountsText(r.lasered_conditions);

const CONDITION_HEADERS = [
  "Claimant",
  "Member ID",
  "Condition",
  "ICD Codes",
  "CPT Codes",
  "Conditional Deductible",
  "Incurred Window",
  "Paid Window",
  "State",
  "Trigger Date",
  "Triggering Claim Item",
  "In Force",
];
const CONDITION_WIDTHS = [30, 18, 26, 22, 18, 20, 24, 24, 12, 14, 38, 10];

const windowText = (start?: string, end?: string): string => {
  if (!start && !end) return "";
  return `${start ?? "—"} – ${end ?? "—"}`;
};

/**
 * Excel export of a policy's lasered claimants: the laser type (as the tab
 * shows it), the straight-laser deductible, then contract-laser detail: contract period (incurred + paid
 * windows), excluded CPTs, and contract deductible. A fully-excluded claimant is
 * flagged and its contract columns left blank (no specific eligibility), but its
 * rows still export.
 */
export const downloadLaseredClaimantsReport = async (
  claimants: LaseredClaimantListItem[],
  policyId: string
): Promise<void> => {
  const workbook = buildLaseredClaimantsWorkbook(claimants);
  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(new Blob([buffer]), `Lasered_Claimants_${policyId.slice(0, 8)}_${today()}.xlsx`);
};

/** Build the workbook (pure; exported for tests). */
export const buildLaseredClaimantsWorkbook = (claimants: LaseredClaimantListItem[]): ExcelJS.Workbook => {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet("Lasered Claimants", SHEET_OPTIONS);
  sheet.columns = COLUMN_WIDTHS.map((width) => ({ width }));

  let rowIdx = 1;

  const titleRow = sheet.getRow(rowIdx++);
  titleRow.height = 32;
  titleRow.getCell(1).value = "Lasered Claimants";
  titleRow.getCell(1).font = { bold: true, size: 14, color: { argb: COLORS.white } };
  titleRow.getCell(1).alignment = { vertical: "middle" };
  for (let col = 1; col <= HEADERS.length; col++) {
    titleRow.getCell(col).fill = { type: "pattern", pattern: "solid", fgColor: { argb: COLORS.primary } };
  }

  sheet.getRow(rowIdx++).height = 8; // spacer

  applyHeaderRow(sheet.getRow(rowIdx++), HEADERS);

  claimants.forEach((r, i) => {
    const row = sheet.getRow(rowIdx++);
    row.height = 22;
    const fill = rowFill(i);
    const cl = r.contract_laser;
    const isExcluded = cl?.is_contract_excluded === true;
    const isContractLasered = cl?.is_contract_lasered === true && !isExcluded;

    row.getCell(1).value = claimantName(r);
    applyDataCellStyle(row.getCell(1), fill);

    row.getCell(2).value = r.member_id ?? "";
    applyDataCellStyle(row.getCell(2), fill);

    // Standard / Contract / Excluded / Conditional, as the tab's Laser Type column.
    row.getCell(3).value = laserKindLabel(r) || "—";
    applyDataCellStyle(row.getCell(3), fill);

    // A conditions-only row has no straight laser; its amount (often 0) is not one.
    const straight = hasStraightLaser(r);
    row.getCell(4).value = straight ? (r.amt_lasered_deductible ?? 0) : "";
    applyDataCellStyle(row.getCell(4), fill, straight);

    // Contract columns are not applicable to a fully-excluded claimant.
    row.getCell(5).value = isContractLasered ? (cl?.amt_contract_lasered_deductible ?? 0) : "";
    applyDataCellStyle(row.getCell(5), fill, isContractLasered);

    row.getCell(6).value = isContractLasered
      ? windowText(cl?.contract_incurred_start_date, cl?.contract_incurred_end_date)
      : "";
    applyDataCellStyle(row.getCell(6), fill);

    row.getCell(7).value = isContractLasered
      ? windowText(cl?.contract_paid_start_date, cl?.contract_paid_end_date)
      : "";
    applyDataCellStyle(row.getCell(7), fill);

    row.getCell(8).value = isContractLasered ? (cl?.excluded_procedures ?? []).join(", ") : "";
    applyDataCellStyle(row.getCell(8), fill);

    row.getCell(9).value = isExcluded ? "Yes" : "";
    applyDataCellStyle(row.getCell(9), fill);

    // Server-resolved deductible (Feature #3502): never recomputed here.
    row.getCell(10).value = r.effective_specific_deductible ?? "";
    applyDataCellStyle(row.getCell(10), fill, r.effective_specific_deductible !== undefined);

    row.getCell(11).value = r.effective_deductible_source ? (SOURCE_LABEL[r.effective_deductible_source] ?? "") : "";
    applyDataCellStyle(row.getCell(11), fill);

    row.getCell(12).value = r.effective_deductible_date ? r.effective_deductible_date.slice(0, 10) : "";
    applyDataCellStyle(row.getCell(12), fill);

    row.getCell(13).value = r.is_deductible_review_pending ? "Yes" : "";
    applyDataCellStyle(row.getCell(13), fill);

    row.getCell(14).value = conditionCounts(r);
    applyDataCellStyle(row.getCell(14), fill);
  });

  // One row per conditional laser (#3515). The claimant sheet stays one row per
  // claimant with a count; this sheet carries each condition's terms and state.
  const withConditions = claimants.filter((r) => (r.lasered_conditions?.length ?? 0) > 0);
  if (withConditions.length > 0) {
    const condSheet = workbook.addWorksheet("Conditional Lasers", SHEET_OPTIONS);
    condSheet.columns = CONDITION_WIDTHS.map((width) => ({ width }));
    applyHeaderRow(condSheet.getRow(1), CONDITION_HEADERS);
    let condRowIdx = 2;
    withConditions.forEach((r) => {
      (r.lasered_conditions ?? []).forEach((c) => {
        const row = condSheet.getRow(condRowIdx);
        const fill = rowFill(condRowIdx);
        condRowIdx++;
        row.height = 22;
        const values: (string | number)[] = [
          claimantName(r),
          r.member_id ?? "",
          c.lasered_condition_name,
          (c.icd_codes ?? []).join(", "),
          (c.cpt_codes ?? []).join(", "),
          c.amt_lasered_conditional_deductible,
          windowText(c.incurred_start_date, c.incurred_end_date),
          windowText(c.paid_start_date, c.paid_end_date),
          formatToTitleCase(conditionDisplayState(c)),
          c.trigger_date ?? "",
          c.triggered_by_claim_item_id ?? "",
          c.is_effective ? "Yes" : "",
        ];
        values.forEach((v, idx) => {
          row.getCell(idx + 1).value = v;
          applyDataCellStyle(row.getCell(idx + 1), fill, idx === 5);
        });
      });
    });
  }

  return workbook;
};
